// src/pages/Home/LastArticlesCommissions.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getLastCommissionArticles } from "../../services/articlescommissionsServices";
import { FaArrowRight, FaTimes, FaChevronLeft, FaChevronRight } from "react-icons/fa";

const ARTICLES_PER_PAGE = 3;

const LastArticlesCommissions = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);

  const [selectedArticle, setSelectedArticle] = useState(null);
  const [zoomImage, setZoomImage] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const latestArticles = await getLastCommissionArticles();
        setArticles(latestArticles || []);
      } catch (error) {
        console.error("Failed to fetch articles", error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  const pageCount = useMemo(
    () => Math.max(1, Math.ceil(articles.length / ARTICLES_PER_PAGE)),
    [articles.length]
  );

  const articlesToDisplay = useMemo(() => {
    if (!articles.length) return [];
    const start = page * ARTICLES_PER_PAGE;
    return articles.slice(start, start + ARTICLES_PER_PAGE);
  }, [articles, page]);

  const handleReadMore = () => {
    navigate("/commissions");
  };

  const goNextPage = () => {
    setPage((prev) => (prev + 1) % pageCount);
  };

  const goPrevPage = () => {
    setPage((prev) => (prev - 1 + pageCount) % pageCount);
  };

  const formatArticleDate = (article) => {
    const rawDate = article.date_article || article.createdAt || article.created_at;
    if (!rawDate) return null;
    return new Date(rawDate).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getArticleImages = (article) => {
    return [article.photo_url1, article.photo_url2, article.photo_url3].filter(
      (u) => u && u !== ""
    );
  };

  const openModal = (article) => {
    setSelectedArticle(article);
    setZoomImage(false);
    setCurrentImageIndex(0);
  };

  const closeModal = () => {
    setSelectedArticle(null);
    setZoomImage(false);
    setCurrentImageIndex(0);
  };

  const handlePreviewContentClick = (e) => {
    const target = e.target;
    if (target && target.closest("a")) {
      e.preventDefault(); // on bloque les liens dans l’aperçu
    }
  };

  const goNextImage = () => {
    if (!selectedArticle) return;
    const images = getArticleImages(selectedArticle);
    if (!images.length) return;
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const goPrevImage = () => {
    if (!selectedArticle) return;
    const images = getArticleImages(selectedArticle);
    if (!images.length) return;
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  /* ========== Loading ========== */
  if (loading) {
    return (
      <section className="py-10 md:py-14 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center text-center mb-6 md:mb-8 animate-pulse">
            <div className="h-6 w-72 bg-slate-200 dark:bg-slate-700 rounded mb-2" />
            <div className="h-4 w-48 bg-slate-200 dark:bg-slate-700 rounded" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="rounded-2xl bg-slate-100 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 h-64"
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  /* ========== Pas d’articles ========== */
  if (!articles.length) {
    return (
      <section className="py-10 md:py-14 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-primary mb-1">
            Commissions
          </p>
          <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight mb-2">
            Derniers articles de commissions
          </h2>
          <p className="text-sm md:text-base text-slate-600 dark:text-slate-400">
            Aucun article de commission n’a été publié pour le moment.
          </p>
        </div>
      </section>
    );
  }

  /* ========== Affichage normal ========== */
  return (
    <>
      <section className="py-10 md:py-14 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Titre */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2 mb-6 md:mb-8">
            <div>
              <p className="text-xs font-semibold tracking-[0.3em] uppercase text-primary mb-1">
                Commissions
              </p>
              <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight">
                Derniers articles de commissions
              </h2>
            </div>
            <p className="text-xs md:text-sm text-slate-600 dark:text-slate-400 max-w-md">
              Informations et comptes rendus des différentes commissions du comité.
            </p>
          </div>

          {/* Cartes : 3 par page */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {articlesToDisplay.map((article) => {
              const images = getArticleImages(article);
              const hasImages = images.length > 0;
              const coverImage = hasImages ? images[0] : "/img/default_image.jpg";
              const commissionName = article.group?.nom || "Commission";

              const articleDate = formatArticleDate(article);

              return (
                <article
                  key={article.id}
                  onClick={() => openModal(article)}
                  className="group flex flex-col bg-white dark:bg-slate-800/90 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden shadow-md hover:shadow-2xl hover:-translate-y-1 transition-all duration-200 cursor-pointer"
                >
                  {/* Image */}
                  <div className="relative h-44 md:h-48 overflow-hidden">
                    <img
                      src={coverImage}
                      alt={article.titre}
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/25 to-transparent pointer-events-none" />

                    {/* Commission */}
                    <div className="absolute left-3 bottom-3 bg-white/95 rounded-full px-3 py-1 text-[11px] font-semibold text-slate-900 shadow-md">
                      {commissionName}
                    </div>
                  </div>

                  {/* Contenu */}
                  <div className="flex flex-col flex-grow p-4 space-y-3">
                    <h3 className="text-base md:text-lg font-semibold text-slate-900 dark:text-slate-50 line-clamp-2">
                      {article.titre}
                    </h3>

                    {articleDate && (
                      <p className="inline-flex items-center text-[11px] font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700/70 rounded-full px-3 py-1 w-fit">
                        Publié le {articleDate}
                      </p>
                    )}

                    {article.contenu && (
                      <div
                        className="text-xs md:text-sm text-slate-700 dark:text-slate-300 line-clamp-5"
                        onClick={handlePreviewContentClick}
                        dangerouslySetInnerHTML={{ __html: article.contenu }}
                      />
                    )}
                  </div>
                </article>
              );
            })}
          </div>

          {/* Pagination + CTA global */}
          <div className="mt-6 md:mt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Pagination pages de 3 */}
            {pageCount > 1 && (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={goPrevPage}
                  className="px-3 py-1.5 rounded-full border border-slate-300 dark:border-slate-600 text-xs text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                >
                  ◀ Précédents
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: pageCount }).map((_, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setPage(idx)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        idx === page
                          ? "bg-primary"
                          : "bg-slate-300 dark:bg-slate-600"
                      }`}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={goNextPage}
                  className="px-3 py-1.5 rounded-full border border-slate-300 dark:border-slate-600 text-xs text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                >
                  Suivants ▶
                </button>
              </div>
            )}

            {/* CTA voir toutes les commissions */}
            <button
              type="button"
              onClick={handleReadMore}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary text-primary text-xs md:text-sm font-semibold uppercase tracking-[0.18em] bg-white dark:bg-slate-900 hover:bg-primary hover:text-white transition-colors"
            >
              <span>Voir toutes les commissions</span>
              <FaArrowRight className="text-sm" />
            </button>
          </div>
        </div>
      </section>

      {/* MODALE ARTICLE COMMISSION */}
      {selectedArticle && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-3 sm:px-4">
          {/* fond sombre */}
          <div
            className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm"
            onClick={closeModal}
          />

          {/* contenu modale */}
          <div
            className="relative max-w-4xl w-full max-h-[90vh] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* bouton fermer */}
            <button
              type="button"
              onClick={closeModal}
              className="absolute top-3 right-3 z-10 inline-flex items-center justify-center w-8 h-8 rounded-full bg-slate-900/80 text-slate-100 hover:bg-slate-800"
              aria-label="Fermer"
            >
              <FaTimes className="w-4 h-4" />
            </button>

            {/* images + zoom si présentes */}
            {(() => {
              const images = getArticleImages(selectedArticle);
              if (!images.length) return null;
              const currentImage = images[currentImageIndex];

              return (
                <div
                  className={`relative bg-black ${
                    zoomImage ? "max-h-[70vh]" : "max-h-72 sm:max-h-80"
                  } overflow-hidden flex items-center justify-center ${
                    zoomImage ? "cursor-zoom-out" : "cursor-zoom-in"
                  }`}
                  onClick={() => setZoomImage((z) => !z)}
                >
                  <img
                    src={currentImage}
                    alt={selectedArticle.titre}
                    className={`w-full h-full transition-all duration-300 ${
                      zoomImage ? "object-contain" : "object-cover"
                    }`}
                  />

                  {/* flèches navigation images */}
                  {images.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          goPrevImage();
                        }}
                        className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-slate-900/80 text-white flex items-center justify-center hover:bg-slate-800"
                      >
                        <FaChevronLeft className="text-lg" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          goNextImage();
                        }}
                        className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-slate-900/80 text-white flex items-center justify-center hover:bg-slate-800"
                      >
                        <FaChevronRight className="text-lg" />
                      </button>
                    </>
                  )}

                  {/* info zoom */}
                  <div className="absolute bottom-3 right-3 bg-black/70 text-white text-[11px] px-3 py-1 rounded-full">
                    Cliquer pour {zoomImage ? "réduire" : "agrandir"}
                  </div>
                </div>
              );
            })()}

            {/* contenu scrollable */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              <div className="mb-3">
                <p className="text-[11px] font-semibold tracking-[0.3em] uppercase text-primary mb-1">
                  Article de commission
                </p>
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 dark:text-slate-50 leading-tight">
                  {selectedArticle.titre}
                </h3>
              </div>

              {/* meta */}
              <div className="flex flex-wrap items-center gap-2 mb-4 text-xs sm:text-[13px] text-slate-600 dark:text-slate-300">
                {selectedArticle.group?.nom && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800/80">
                    {selectedArticle.group.nom}
                  </span>
                )}
                {formatArticleDate(selectedArticle) && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-slate-50 dark:bg-slate-800/80">
                    Publié le {formatArticleDate(selectedArticle)}
                  </span>
                )}
              </div>

              {/* contenu complet */}
              {selectedArticle.contenu && (
                <div
                  className="prose prose-sm sm:prose-base max-w-none prose-headings:mt-4 prose-a:text-primary dark:prose-invert"
                  dangerouslySetInnerHTML={{ __html: selectedArticle.contenu }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LastArticlesCommissions;
